import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { wholeNumberWithCommas } from "~/components/utils/NumberFormatter";
import { FALLBACK_FONT, fontStyle, registerInter } from "~/components/utils/pdfFont";

/*
PURPOSE:
The drawing kit the submission PDFs are built from — the receipt and the pre-submission summary
render the same components on screen,
*/

// The sass theme values from stylesheets/theme/_colors.sass.
export const COLORS = {
  title: [10, 22, 47], // $blue-900 / $title
  subtitle: [74, 91, 105], // $subtitle
  border: [222, 229, 235], // $border
  blue: [5, 96, 253], // $blue-500
  blueLight: [236, 241, 255], // $blue-100
  white: [255, 255, 255],
  green: [36, 182, 104], // $heavy-green
  red: [245, 0, 87], // $red
  orange: [255, 112, 82], // $orange
};

/*
On screen `.submission_preview--receipt` is 980px wide with 24px of padding, so its content
column is 932px. The summary renders the same components in a panel of about that width, and
both documents reduce from it, so a card is the same size in either.
*/
const PAGE_CONTENT_PX = 932;
const PT_PER_MM = 1 / 0.352778;

// `line-height: normal` for Inter, and the distance from a line box's top to its baseline.
export const LINE = 1.25;
const BASELINE = 0.99;

// One token per class the pages style these documents with; sizes are `font-size` in page pixels.
export const TYPE = {
  // .submission_preview__subtitle — the eyebrow above the name, and every card's title
  cardTitle: { px: 12, style: "medium", color: COLORS.subtitle },
  // .submission_preview__header__details__title
  heading: { px: 20, style: "medium" },
  // .submission_preview__divider__title
  dividerTitle: { px: 12, style: "bold" },
  // .submission_preview__title
  body: { px: 12 },
  bodyMuted: { px: 12, color: COLORS.subtitle },
  // .submission_preview__title--bold
  bodyStrong: { px: 12, style: "medium" },
  // .submission_preview__title--lg
  bodyLarge: { px: 16 },
  bodyLargeStrong: { px: 16, style: "medium" },
  // .submission_preview__description
  description: { px: 10 },
  descriptionMuted: { px: 10, color: COLORS.subtitle },
  // .submission_preview__stat_value
  statValue: { px: 16, style: "medium" },
  // .submission_preview__stat_value--sm
  statValueSmall: { px: 10, style: "medium" },
  // .submission_preview__scroll-list
  listItem: { px: 10, style: "medium" },
  // .submission_preview__user__avatar__initials
  avatarInitials: { px: 16, style: "medium", color: COLORS.blue },
  // .badge
  badge: { px: 10, style: "medium" },
  // .submission_preview__notice
  notice: { px: 12, color: COLORS.blue },
  // .summary_table th / td
  tableHead: { px: 12, style: "medium" },
  tableBody: { px: 12 },
  // No counterpart on the page: the printed page number and document name.
  footer: { px: 11, color: COLORS.subtitle },
};

export const PAGE_MARGIN = 15;
export const CARD_RADIUS_PX = 8;
export const CARD_PADDING_PX = 16;
export const CONTENT_GAP_PX = 20;
const DIVIDER_MARGIN_PX = 40;
export const FOOTER_RESERVE = 20;

export const count = (value) => wholeNumberWithCommas(value ?? 0);

// A shared cursor/geometry object threaded through the section renderers.
function createLayout(doc) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const contentWidth = pageWidth - PAGE_MARGIN * 2;
  const scale = contentWidth / PAGE_CONTENT_PX;

  return {
    doc,
    // Replaced with Inter once its data has loaded; see `registerInter`.
    font: FALLBACK_FONT,
    margin: PAGE_MARGIN,
    pageWidth,
    pageHeight: doc.internal.pageSize.getHeight(),
    contentWidth,
    // Page pixels to millimetres, and to points for font sizes.
    u: (px) => px * scale,
    pt: (px) => px * scale * PT_PER_MM,
    y: 0,
  };
}

// A document with the page's typeface embedded and its 1px hairline set as the line width.
export async function createDocument() {
  const doc = new jsPDF();
  const layout = createLayout(doc);

  layout.font = await registerInter(doc);
  doc.setLineWidth(layout.u(1));

  return layout;
}

// Starts a new page when `needed` mm won't fit above the footer.
export function ensureSpace(layout, needed) {
  if (layout.y + needed <= layout.pageHeight - FOOTER_RESERVE) return;

  layout.doc.addPage();
  layout.doc.setLineWidth(layout.u(1));
  layout.y = PAGE_MARGIN;
}

export function setFont(layout, { px, style = "normal", color = COLORS.title }) {
  layout.doc.setFontSize(layout.pt(px));
  layout.doc.setFont(layout.font, fontStyle(layout.font, style));
  layout.doc.setTextColor(...color);
}

export function wrap(layout, token, text, width) {
  setFont(layout, token);

  return width ? layout.doc.splitTextToSize(String(text ?? ""), width) : [String(text ?? "")];
}

// The height a run of text occupies, in mm, so a card can be sized before it is drawn.
export function textHeight(layout, token, lines) {
  return lines * layout.u(token.px * LINE);
}

/*
Draws text the way the page lays it out: `top` is the top of the first line box rather than a
baseline, so callers can stack blocks by adding up the heights returned. `width` wraps.
*/
export function drawText(layout, token, text, x, top, { width, maxLines = 3, align } = {}) {
  const lines = wrap(layout, token, text, width).slice(0, maxLines);
  const lineHeight = layout.u(token.px * LINE);
  const options = align ? { align } : undefined;

  lines.forEach((line, index) => {
    layout.doc.text(line, x, top + layout.u(token.px * BASELINE) + index * lineHeight, options);
  });

  return lines.length * lineHeight;
}

// The page's `submission_preview__divider` — a rule with an optional centred uppercase title.
// `keepWith` is the height of the block the rule introduces, so a title never orphans.
export function drawDivider(layout, title = null, keepWith = 0) {
  const { doc, margin, pageWidth, u } = layout;
  const spacing = u(DIVIDER_MARGIN_PX);

  ensureSpace(layout, spacing * 2 + keepWith);

  layout.y += spacing;

  doc.setDrawColor(...COLORS.border);
  doc.line(margin, layout.y, pageWidth - margin, layout.y);

  if (title) {
    const label = title.toUpperCase();
    setFont(layout, TYPE.dividerTitle);
    const labelWidth = doc.getTextWidth(label);
    const centre = pageWidth / 2;
    const padding = u(12);
    const labelHeight = u(TYPE.dividerTitle.px * LINE);

    // Punch a gap in the rule so the label sits inside it, as the CSS does with a white bg.
    doc.setFillColor(...COLORS.white);
    doc.rect(
      centre - labelWidth / 2 - padding,
      layout.y - labelHeight / 2,
      labelWidth + padding * 2,
      labelHeight,
      "F"
    );
    drawText(layout, TYPE.dividerTitle, label, centre, layout.y - labelHeight / 2, {
      align: "center",
    });
  }

  layout.y += spacing;
}

// The `submission_preview__card` chrome. Stroked separately from its contents so cards that
// share a row can be squared off to the tallest of them, as the page's flex row does.
export function drawCard(layout, x, y, width, height) {
  const radius = layout.u(CARD_RADIUS_PX);
  layout.doc.setDrawColor(...COLORS.border);
  layout.doc.roundedRect(x, y, width, height, radius, radius, "S");
}

// A card title in its own padded block above a full-width rule, as the summary cards open.
export function drawCardHeader(layout, title, x, y, width) {
  const padding = layout.u(CARD_PADDING_PX);

  drawText(layout, TYPE.cardTitle, title, x + padding, y + padding);

  const height = padding * 2 + textHeight(layout, TYPE.cardTitle, 1);
  layout.doc.setDrawColor(...COLORS.border);
  layout.doc.line(x, y + height, x + width, y + height);

  return height;
}

// The width of each card in a `submission_preview__content` row of `columns` equal cards.
export function columnWidth(layout, columns) {
  const gap = layout.u(CONTENT_GAP_PX);

  return (layout.contentWidth - gap * (columns - 1)) / columns;
}

export function columnX(layout, index, width) {
  return layout.margin + index * (width + layout.u(CONTENT_GAP_PX));
}

/*
Tracks where a card wrapped around a table has to be closed off. A table long enough to spill
over splits, and its card has to split with it — one box per page, rather than a single box
measured from a `y` that belongs to a page already behind us. autoTable's page hook fires
while the document is still on the page it has just filled, so the page number is read there.
*/
export function cardSegments(layout, top) {
  const segments = [];
  let segmentTop = top;

  const onPage = ({ doc, cursor }) => {
    segments.push({
      page: doc.internal.getCurrentPageInfo().pageNumber,
      top: segmentTop,
      y: cursor.y,
    });
    segmentTop = PAGE_MARGIN;
  };

  return { segments, onPage };
}

export function drawCardSegments(layout, x, width, segments) {
  const { doc } = layout;
  const current = doc.internal.getCurrentPageInfo().pageNumber;

  segments.forEach(({ page, top, y }) => {
    doc.setPage(page);
    drawCard(layout, x, top, width, y - top);
  });

  doc.setPage(current);
}

// Shared table styling so every table matches the page's `.summary_table`: a header band in
// $border, no zebra striping, and a hairline between body rows.
export function drawTable(
  layout,
  { head, body, x = layout.margin, width = layout.contentWidth, columnStyles, hooks = {} }
) {
  const { doc, u, pt } = layout;
  const lastRow = body.length - 1;

  autoTable(doc, {
    startY: layout.y,
    // A table that spills over resumes at the top margin and stops clear of the footer.
    margin: {
      left: x,
      right: layout.pageWidth - x - width,
      top: PAGE_MARGIN,
      bottom: FOOTER_RESERVE,
    },
    tableWidth: width,
    head,
    body,
    theme: "plain",
    styles: {
      font: layout.font,
      fontSize: pt(TYPE.tableBody.px),
      textColor: COLORS.title,
      cellPadding: u(12),
      lineColor: COLORS.border,
      lineWidth: 0,
    },
    headStyles: {
      fillColor: COLORS.border,
      fontStyle: fontStyle(layout.font, TYPE.tableHead.style),
    },
    // The page rules off every row but the last, so the card's own border closes the table.
    bodyStyles: { lineWidth: { bottom: u(1) } },
    didParseCell: (data) => {
      if (data.section === "body" && data.row.index === lastRow) data.cell.styles.lineWidth = 0;
    },
    columnStyles,
    ...hooks,
  });

  layout.y = doc.lastAutoTable.finalY;
}

// autoTable sizes rows from the font and padding it is given; this mirrors that arithmetic so
// a section can check it fits on the page before any of it is drawn.
export function tableHeight(layout, rows, { rowPadding = 12 } = {}) {
  const row = (padding) => layout.u(TYPE.tableBody.px * 1.15 + padding * 2);

  return row(12) + rows * row(rowPadding);
}

// A table filling a card of its own, as the summary's file list and the entity blocks do.
// Draws the card around it, following the table onto later pages if it spills over.
export function drawTableCard(layout, options) {
  const top = layout.y;
  const x = options.x ?? layout.margin;
  const width = options.width ?? layout.contentWidth;
  const card = cardSegments(layout, top);

  drawTable(layout, { ...options, hooks: { ...options.hooks, didDrawPage: card.onPage } });

  if (card.segments.length) drawCardSegments(layout, x, width, card.segments);
  else drawCard(layout, x, top, width, layout.y - top);
}

/*
The `submission_preview__header`: an eyebrow, the document's name and its date, with the user
it belongs to set against the right margin — avatar initials beside their name and email.
*/
export function renderHeader(layout, { eyebrow, title, subtitle, user = {} }) {
  const { doc, margin, pageWidth, u } = layout;
  const top = PAGE_MARGIN;

  let y = top;
  y += drawText(layout, TYPE.cardTitle, eyebrow.toUpperCase(), margin, y);
  // The eyebrow sits 4px further off the title than the flex gap alone puts it.
  y += u(8);
  y += drawText(layout, TYPE.heading, title || "", margin, y);
  y += u(4);
  y += drawText(layout, TYPE.body, subtitle || "", margin, y);

  // The page centres the user against the details column, so measure it first.
  const middle = (top + y) / 2;
  const avatarRadius = u(20);
  const lineHeight = u(TYPE.body.px * LINE);
  const textTop = middle - (lineHeight * 2 + u(2)) / 2;

  // Measured, not reserved: the block hangs off the right margin however wide its
  // longest line happens to be, so it always meets the page edge as the page's does.
  setFont(layout, TYPE.body);
  const textWidth = Math.max(doc.getTextWidth(user.name || ""), doc.getTextWidth(user.email || ""));
  const textX = pageWidth - margin - textWidth;

  // A partner submitting on an entity's behalf has no initials, and the page hides the avatar.
  if (user.initials) {
    const centre = textX - u(8) - avatarRadius;
    doc.setFillColor(...COLORS.blueLight);
    doc.circle(centre, middle, avatarRadius, "F");
    setFont(layout, TYPE.avatarInitials);
    doc.text(user.initials, centre, middle + u(TYPE.avatarInitials.px * 0.36), { align: "center" });
  }

  drawText(layout, TYPE.body, user.name || "", textX, textTop);
  drawText(layout, TYPE.body, user.email || "", textX, textTop + lineHeight + u(2));

  layout.y = Math.max(y, middle + avatarRadius);
}

// A row of `SummaryCard`s: uppercase title, wrapped description, big value.
export function renderStatCards(layout, cards) {
  if (!cards?.length) return;

  const { u } = layout;
  const width = columnWidth(layout, cards.length);
  const padding = u(CARD_PADDING_PX);
  const textWidth = width - padding * 2;

  // The cards sit in a flex row, so they all take the height of the wordiest description.
  const descriptions = cards.map((stat) =>
    wrap(layout, TYPE.description, stat.description, textWidth).slice(0, 3)
  );
  const bodyHeight = Math.max(
    ...descriptions.map((lines) => textHeight(layout, TYPE.description, lines.length))
  );
  const height =
    padding * 2 +
    textHeight(layout, TYPE.cardTitle, 1) +
    u(4) +
    bodyHeight +
    u(8) +
    textHeight(layout, TYPE.statValue, 1);

  ensureSpace(layout, height);

  cards.forEach((stat, index) => {
    const x = columnX(layout, index, width);
    drawCard(layout, x, layout.y, width, height);

    let y = layout.y + padding;
    y += drawText(layout, TYPE.cardTitle, String(stat.title || "").toUpperCase(), x + padding, y);
    y += u(4);

    drawText(layout, TYPE.description, stat.description || "", x + padding, y, {
      width: textWidth,
    });
    // Every card reserves the same room here, so their values line up across the row.
    y += bodyHeight;

    // `stat_value` carries a top margin of its own.
    drawText(layout, TYPE.statValue, count(stat.value), x + padding, y + u(8));
  });

  layout.y += height;
}

/*
One `EntitySummary`: a header pairing the entity's identifying lines with its row count, a five
column strip of stats, then a table flush to the card's edges. `lines` is the left of that
header as `{ token, text }` pairs, which is the one thing the two documents disagree on — the
summary labels the ID, the receipt leads with it and names the entity underneath.
*/
export function renderEntityCard(
  layout,
  { lines, rowCount, conformingPercentage, stats = [], table }
) {
  const { doc, margin, contentWidth, u } = layout;
  const padding = u(CARD_PADDING_PX);
  const rows = table?.body || [];

  const headerLines = lines.reduce((total, { token }) => total + textHeight(layout, token, 1), 0);
  const headerHeight = padding * 2 + headerLines + u(4) * (lines.length - 1);
  const statsHeight =
    padding * 2 +
    textHeight(layout, TYPE.cardTitle, 1) +
    u(8) +
    textHeight(layout, TYPE.statValue, 1);

  /*
  `entity_summary` carries a top margin. The block keeps its header, stats and the first rows
  of its table together; past that the table splits and its card follows it, so there is no
  point holding back more than starts it off.
  */
  layout.y += u(16);
  ensureSpace(layout, headerHeight + statsHeight + tableHeight(layout, Math.min(rows.length, 2)));

  const top = layout.y;
  let y = top + padding;

  lines.forEach(({ token, text }, index) => {
    if (index > 0) y += u(4);
    y += drawText(layout, token, text, margin + padding, y);
  });

  // The numbers column is set against the card's right edge, top-aligned with the lines.
  let numbersY = top + padding;
  numbersY += drawText(
    layout,
    TYPE.bodyLargeStrong,
    `${count(rowCount)} rows`,
    layout.pageWidth - margin - padding,
    numbersY,
    { align: "right" }
  );

  if (conformingPercentage != null) {
    drawText(
      layout,
      TYPE.body,
      `${conformingPercentage}% Conforming`,
      layout.pageWidth - margin - padding,
      numbersY + u(4),
      { align: "right" }
    );
  }

  doc.setDrawColor(...COLORS.border);
  doc.line(margin, top + headerHeight, margin + contentWidth, top + headerHeight);

  // The page's stats grid is five columns wide however few stats there are.
  const statWidth = contentWidth / 5;
  stats.forEach((stat, index) => {
    const x = margin + (index % 5) * statWidth + padding;
    const statY = top + headerHeight + padding + Math.floor(index / 5) * statsHeight;

    drawText(layout, TYPE.cardTitle, String(stat.title || "").toUpperCase(), x, statY);
    drawText(
      layout,
      TYPE.statValue,
      count(stat.count),
      x,
      statY + textHeight(layout, TYPE.cardTitle, 1) + u(8)
    );
  });

  layout.y = top + headerHeight + statsHeight * Math.max(Math.ceil(stats.length / 5), 1);

  if (!rows.length) {
    drawCard(layout, margin, top, contentWidth, layout.y - top);
    return;
  }

  // An entity can list hundreds of NDCs, so this table is the one that runs over pages.
  const card = cardSegments(layout, top);

  drawTable(layout, { ...table, hooks: { didDrawPage: card.onPage } });

  if (card.segments.length) drawCardSegments(layout, margin, contentWidth, card.segments);
  else drawCard(layout, margin, top, contentWidth, layout.y - top);
}

// The `submission_preview__notice` callout. The page leads it with an icon, which the embedded
// font has no glyph for, so the border and colour carry the meaning on their own.
export function renderNotice(layout, text, { warning = false } = {}) {
  const { u } = layout;
  const padding = u(12);
  const color = warning ? COLORS.orange : COLORS.blue;
  const width = layout.contentWidth;
  const lines = wrap(layout, TYPE.notice, text, width - padding * 2);
  const height = padding * 2 + textHeight(layout, TYPE.notice, lines.length);

  ensureSpace(layout, height);

  layout.doc.setDrawColor(...color);
  layout.doc.roundedRect(
    layout.margin,
    layout.y,
    width,
    height,
    u(CARD_RADIUS_PX),
    u(CARD_RADIUS_PX),
    "S"
  );
  drawText(layout, { ...TYPE.notice, color }, text, layout.margin + padding, layout.y + padding, {
    width: width - padding * 2,
  });

  layout.y += height;
}

export function renderFooter(layout, name) {
  const { doc } = layout;
  const pageCount = doc.internal.getNumberOfPages();

  for (let page = 1; page <= pageCount; page++) {
    doc.setPage(page);
    setFont(layout, TYPE.footer);
    doc.text(`340B ESP — ${name}`, layout.margin, layout.pageHeight - 8);
    doc.text(
      `Page ${page} of ${pageCount}`,
      layout.pageWidth - layout.margin,
      layout.pageHeight - 8,
      {
        align: "right",
      }
    );
  }
}
